/**
 * ★★ THE ONE CROSSING POINT FROM THIS APP INTO `shared/content-model`.
 *
 * `02-nfr.md` and `adr/0008-editor.md` put the block union, the converters and the D18
 * anchor maths in that package for ONE reason: *"the editor AND the moderation worker must compute
 * identical anchors"*, and `test/shared/content-model/anchor-agreement.spec.ts` asserts that of the
 * two real call sites over every span of every generated document. **This screen is one of those
 * callers**, so it reaches those functions rather than re-deriving them — a second implementation
 * of `sliceCp` in a component is the drift the agreement test exists to catch, and it would pass
 * that test by never being seen by it.
 *
 * WHY EVERY CALL GOES THROUGH THIS FILE AND NOT THROUGH THE COMPONENTS. Three things have to be
 * said once rather than at six call sites:
 *
 *   1. **THE IMPORT IS RELATIVE AND DEEP, AND THAT IS THE SANCTIONED WAY.** `@moonx/content-model`
 *      is a workspace package and is NOT a dependency of `@moonx/authors-web`, so it is not linked
 *      into this app's `node_modules` and the package name would not resolve. adr/0013 rules "no
 *      path aliases — plain relative imports", and ./generated.ts already crosses to
 *      `shared/` the same way and explains it at length.
 *   2. **THE CONTRACT'S `Block` AND THE PACKAGE'S `CountableBlock` ARE NOT THE SAME TYPE.** The
 *      package takes `{ body: string | null, quoteAttribution: string | null }` with both members
 *      REQUIRED — `text-measure.ts` says why: under `exactOptionalPropertyTypes` a caller
 *      who forgets one would silently count an attribution as zero words. The contract's union
 *      makes both optional and absent on two of the four kinds. That conversion is here, once.
 *   3. **A DIVIDER HAS NO TEXT AND AN IMAGE'S TEXT IS ITS CAPTION.** Captions count as words, which
 *      they did not in the legacy editor (authors-api.yaml's ImageBlock `caption`), and getting
 *      that wrong makes the editor's number differ from the submit guard's for image-heavy pieces.
 *
 * ★ REPORTED, NOT WORKED AROUND: importing `relocate` pulls `anchors.ts`, which imports three
 * VALUES from `blocks.ts`, which imports `zod` — so `zod` enters the cabinet bundle. It is a real
 * consequence of using the shared maths rather than copying it, it is the right trade, and it is
 * named in the notes beside router.tsx's bundle paragraph rather than hidden.
 */
import {
  countTextLength,
  countTitleLength,
  countWords,
} from '../../../../../../shared/content-model/text-measure';
import { codePointLength, relocate, sliceCp } from '../../../../../../shared/content-model/anchors';
import type { CountableArticle } from '../../../../../../shared/content-model/text-measure';
import type { Relocation, StoredSpan } from '../../../../../../shared/content-model/anchors';
import type { Block, ReviewFinding, WorkingCopy } from './editor-api';

export { codePointLength, countTextLength, countTitleLength, sliceCp };
export type { Relocation };

/**
 * The plain text of a block, or null where it has none.
 *
 * A TEXT AND A QUOTE CARRY `body`; AN IMAGE CARRIES ITS `caption`; A DIVIDER CARRIES NOTHING. The
 * caption is the one that surprises: it is the block's own text for counting AND it is what a
 * span anchor into an image block would be measured over.
 */
export function blockText(block: Block): string | null {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return block.body;
    case 'image':
      return block.caption ?? null;
    case 'divider':
    case 'video':
      return null;
    default:
      // `Block` is a closed discriminated union; this makes a sixth kind a compile error.
      return null;
  }
}

/** A quote's attribution, which counts toward the word count. Null on every other kind. */
export function blockAttribution(block: Block): string | null {
  return block.kind === 'quote' ? (block.quoteAttribution ?? null) : null;
}

/**
 * THE word count, as the submit guard measures it: whitespace-split tokens over the title, the
 * subtitle, every block's plain text and each quote attribution, NFC-normalised, with each Chinese
 * or Japanese character one word (`text-measure.ts`). The editor shows this number and the guard
 * uses this function, which is what stops the blockers banner promising a submit the server then
 * refuses.
 */
export function articleWordCount(article: {
  readonly title: string;
  readonly subtitle: string;
  readonly blocks: readonly Block[];
}): number {
  const countable: CountableArticle = {
    title: article.title,
    subtitle: article.subtitle,
    blocks: article.blocks.map((block) => ({
      body: blockText(block),
      quoteAttribution: blockAttribution(block),
    })),
  };
  return countWords(countable);
}

/** The same, taken straight off a working copy. */
export function workingCopyWordCount(copy: WorkingCopy): number {
  return articleWordCount({ title: copy.title, subtitle: copy.subtitle, blocks: copy.blocks });
}

/**
 * ★ WHERE A FINDING POINTS **NOW**, rather than where it pointed when the verdict was written.
 *
 * The server sends `anchorState` as of the version it judged. The author has been editing since,
 * so the editor re-runs the SAME relocation the moderation worker would (`anchors.ts`) over
 * the block's current text, and renders the answer it gets:
 *
 *   - `position` — the offsets still land on the stored quote. Highlight them.
 *   - `quote`    — the text moved and the quote was found somewhere else. Highlight there.
 *   - `null`     — the quote is gone. The finding degrades to unanchored AND KEEPS ITS QUOTE, so
 *                  the author is still shown what was meant (`ReviewFinding.quoteExact`).
 *
 * Returns null for anything that is not a span anchor with a quote — an article- or block-scoped
 * finding has nothing to relocate, which is not a failure.
 */
export function relocateFinding(finding: ReviewFinding, currentText: string): Relocation | null {
  if (finding.anchorScope !== 'span') {
    return null;
  }
  const quoteExact = finding.quoteExact;
  if (quoteExact === undefined || quoteExact === null) {
    return null;
  }
  const span: StoredSpan = {
    startCp: finding.startCp ?? 0,
    endCp: finding.endCp ?? codePointLength(quoteExact),
    quoteExact,
    quotePrefix: finding.quotePrefix ?? null,
    quoteSuffix: finding.quoteSuffix ?? null,
  };
  return relocate(span, currentText);
}
